/**
 * Earth surface shader — blends NASA day/night textures with sun lighting,
 * adds subtle holographic grid overlay and fresnel rim glow.
 */

export const earthVertexShader = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPos;
  varying vec3 vPosition;
  varying float vFresnel;
  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    vPosition = position;
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    vFresnel = 1.0 - abs(dot(viewDir, normalize((modelMatrix * vec4(normal, 0.0)).xyz)));
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const earthFragmentShader = /* glsl */ `
  uniform float time;
  uniform sampler2D dayMap;
  uniform sampler2D nightMap;
  uniform sampler2D bumpMap;
  uniform vec3 glowColor;
  uniform vec3 sunDir;
  varying vec2 vUv;
  varying vec3 vNormal;
  varying vec3 vWorldPos;
  varying vec3 vPosition;
  varying float vFresnel;

  void main() {
    vec3 dayColor = texture2D(dayMap, vUv).rgb;
    vec3 nightColor = texture2D(nightMap, vUv).rgb;
    float bump = texture2D(bumpMap, vUv).r;

    float sunDot = dot(vNormal, sunDir);
    float dayFactor = smoothstep(-0.15, 0.3, sunDot);

    vec3 earthColor = mix(nightColor * 1.5, dayColor, dayFactor);

    // Subtle holographic grid overlay
    float lat = abs(fract(vPosition.y * 8.0) - 0.5) * 2.0;
    lat = smoothstep(0.92, 0.97, lat);
    float lon = abs(fract(atan(vPosition.x, vPosition.z) * 5.093) - 0.5) * 2.0;
    lon = smoothstep(0.92, 0.97, lon);
    float grid = max(lat, lon) * 0.08;

    // Data flow scan line
    float flow = sin(vPosition.y * 25.0 + time * 1.5) * 0.5 + 0.5;
    flow = smoothstep(0.85, 1.0, flow) * 0.06;

    // Edge fresnel rim
    float edge = pow(vFresnel, 3.5);
    vec3 rimColor = glowColor * edge * 0.25;

    vec3 finalColor = earthColor + rimColor + glowColor * (grid + flow);
    finalColor += dayColor * bump * 0.08 * dayFactor;

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

/** Atmosphere glow — thin edge-only fresnel. */
export const atmosVertexShader = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vWorldPos;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const atmosFragmentShader = /* glsl */ `
  uniform vec3 glowColor;
  uniform float time;
  varying vec3 vNormal;
  varying vec3 vWorldPos;
  void main() {
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    float fresnel = 1.0 - dot(viewDir, vNormal);
    fresnel = pow(fresnel, 5.0);
    gl_FragColor = vec4(glowColor, fresnel * 0.2);
  }
`;
